import React, { useState } from 'react';
import './Admissions.css';
import brochure from '../files/brochure.pdf';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GRADES = [
  { value: '', label: 'Select Grade' },
  { value: 'creche', label: 'Crèche' },
  { value: 'nursery-1', label: 'Nursery 1' },
  { value: 'nursery-2', label: 'Nursery 2' },
  { value: 'pre-k', label: 'Pre-K / Pre-Primary' },
  { value: 'primary-1', label: 'Primary 1' },
  { value: 'primary-2', label: 'Primary 2' },
  { value: 'primary-3', label: 'Primary 3' },
  { value: 'primary-4', label: 'Primary 4' },
  { value: 'primary-5', label: 'Primary 5' },
  { value: 'primary-6', label: 'Primary 6' },
];

const STEPS = [
  { num: '01', title: 'Fill the Form', text: 'Complete the online enquiry form below or visit us in person.' },
  { num: '02', title: 'Submit Documents', text: 'Provide birth certificate and previous school records if applicable.' },
  { num: '03', title: 'Interview', text: 'Attend a brief, friendly assessment with our admissions team.' },
  { num: '04', title: 'Decision', text: 'Receive your admission decision within 2 weeks.' },
];

function Admissions() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', childName: '', grade: '', message: '',
  });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      const res = await fetch(`${API_BASE}/submissions/admission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed.');
      setStatus('success');
      setForm({ name: '', email: '', phone: '', childName: '', grade: '', message: '' });
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  return (
    <div className="admissions-page">

      {/* Hero */}
      <section className="page-hero admissions-hero">
        <h1>Admissions</h1>
        <p>Join the Twinkle Toes family and give your child the very best start.</p>
      </section>

      {/* Process steps */}
      <section className="admissions-process section">
        <div className="container">
          <h2 className="section-title">How to Apply</h2>
          <p className="section-subtitle">Our admissions process is straightforward and welcoming. Here's what to expect.</p>
          <div className="process-steps">
            {STEPS.map((step) => (
              <div key={step.num} className="process-step">
                <div className="process-step-num" aria-hidden="true">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + info */}
      <section className="admissions-content section">
        <div className="container admissions-grid">
          {/* Info */}
          <div className="admissions-info">
            <h2>Visit Us</h2>
            <div className="admissions-info-card">
              <div className="admissions-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <div>
                  <strong>Location</strong>
                  <p>Cajaah Estate, Orozo, Abuja</p>
                </div>
              </div>
              <div className="admissions-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <div>
                  <strong>Office Hours</strong>
                  <p>Monday – Friday, 8:00 AM – 5:00 PM</p>
                </div>
              </div>
              <div className="admissions-detail">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 12a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1.84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                <div>
                  <strong>Phone</strong>
                  <p><a href="tel:+2348012345678">+234 801 234 5678</a></p>
                </div>
              </div>
            </div>
            <div className="admissions-brochure">
              <p>Want a full overview of our school? Download our brochure.</p>
              <a href={brochure} download className="btn btn-gold">Download Brochure</a>
            </div>
          </div>

          {/* Form */}
          <div className="admissions-form-wrap">
            <h2>Request More Information</h2>
            {status === 'success' ? (
              <div className="form-feedback success admissions-success">
                <strong>Enquiry received!</strong> Our admissions team will contact you within 1–2 business days.
              </div>
            ) : (
              <form className="admissions-form" onSubmit={handleSubmit} noValidate>
                {status === 'error' && <div className="form-feedback error">{error}</div>}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="adm-name">Parent/Guardian Name *</label>
                    <input id="adm-name" name="name" type="text" value={form.name} onChange={handleChange} required placeholder="Full name" autoComplete="name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="adm-phone">Phone Number *</label>
                    <input id="adm-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required placeholder="+234 …" autoComplete="tel" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="adm-email">Email Address</label>
                  <input id="adm-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" autoComplete="email" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="adm-child">Child's Name</label>
                    <input id="adm-child" name="childName" type="text" value={form.childName} onChange={handleChange} placeholder="Child's full name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="adm-grade">Grade Applying For</label>
                    <select id="adm-grade" name="grade" value={form.grade} onChange={handleChange}>
                      {GRADES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="adm-message">Additional Information</label>
                  <textarea id="adm-message" name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Any questions or additional notes…" />
                </div>
                <button type="submit" className="btn btn-gold" disabled={status === 'loading'} aria-busy={status === 'loading'}>
                  {status === 'loading' ? 'Submitting…' : 'Submit Enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Admissions;